import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase, getSafeSession } from '@/lib/supabaseClient';
import { useProfile } from '@/hooks/useProfile';

export default function Account() {
  const { profile, loading } = useProfile();
  const [email, setEmail] = useState('');

  // 이름 변경
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState('');
  const [nameError, setNameError] = useState('');

  // 비밀번호 변경
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    if (profile) setName(profile.full_name || '');
  }, [profile]);

  useEffect(() => {
    getSafeSession().then((session) => {
      setEmail(session?.user?.email || '');
    });
  }, []);

  const roleLabel =
    profile?.role === 'operator' ? '운영자' : profile?.role === 'owner' ? '원장님' : '선생님';

  async function handleSaveName(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setNameError('');
    setNameMsg('');
    if (!name.trim()) {
      setNameError('이름을 입력해 주세요.');
      return;
    }
    setSavingName(true);
    const { error } = await supabase
      .from('profiles')
      .update({ full_name: name.trim() })
      .eq('id', profile?.id);
    setSavingName(false);
    if (error) {
      setNameError('이름 변경 중 문제가 발생했어요. 다시 시도해 주세요.');
      console.error(error);
      return;
    }
    setNameMsg('이름이 변경됐어요.');
  }

  async function handleChangePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPwError('');
    setPwMsg('');
    if (password.length < 6) {
      setPwError('비밀번호는 6자 이상이어야 해요.');
      return;
    }
    if (password !== confirmPassword) {
      setPwError('새 비밀번호가 일치하지 않아요.');
      return;
    }
    setSavingPw(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSavingPw(false);
    if (error) {
      setPwError('비밀번호 변경 중 문제가 발생했어요. 다시 로그인 후 시도해 주세요.');
      console.error(error);
      return;
    }
    setPassword('');
    setConfirmPassword('');
    setPwMsg('비밀번호가 변경됐어요. 다음 로그인부터 새 비밀번호를 사용하세요.');
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground-950">내 계정</h2>
        <p className="mt-1 text-sm text-foreground-500">내 정보를 확인하고 이름과 비밀번호를 변경할 수 있어요.</p>
      </div>

      {/* 내 정보 */}
      <div className="rounded-lg border border-background-200 bg-white p-5">
        <h4 className="text-sm font-semibold text-foreground-700">내 정보</h4>
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <span className="text-base font-bold">{(name || '이').charAt(0)}</span>
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">{name || '이름 없음'}</p>
              <p className="text-xs text-foreground-500">{email}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-background-100 pt-3">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {roleLabel}
            </span>
            {profile?.academy_name && (
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                {profile.academy_name}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 이름 변경 */}
      <div className="rounded-lg border border-background-200 bg-white p-5">
        <h4 className="text-sm font-semibold text-foreground-700">이름 변경</h4>
        <form onSubmit={handleSaveName} className="mt-4 space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">이름</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="이름을 입력하세요"
              className="w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
          {nameError && <p className="text-sm text-accent-700">{nameError}</p>}
          {nameMsg && <p className="text-sm text-primary-700">{nameMsg}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingName}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingName ? '저장 중...' : '이름 저장'}
            </button>
          </div>
        </form>
      </div>

      {/* 비밀번호 변경 */}
      <div className="rounded-lg border border-background-200 bg-white p-5">
        <h4 className="text-sm font-semibold text-foreground-700">비밀번호 변경</h4>
        <form onSubmit={handleChangePassword} className="mt-4 space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">새 비밀번호</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6자 이상 입력하세요"
              autoComplete="new-password"
              className="w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">새 비밀번호 확인</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="한 번 더 입력하세요"
              autoComplete="new-password"
              className="w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </div>
          {pwError && <p className="text-sm text-accent-700">{pwError}</p>}
          {pwMsg && <p className="text-sm text-primary-700">{pwMsg}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingPw}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingPw ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}